import { useCallback, useState } from 'react'
import { BusinessTimesKey, BusinessTimeType, HandleBusinessTimeType, OpenTimeType } from 'types/store'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { useFormatBusinessTimes } from 'hooks/businessHour'
import { useSelector } from 'react-redux'
import { State } from 'index'
import {
  getLastOrderTime,
  getLastPickupTime,
  getOpenTime,
  getOrderStartTime,
  getRelativePickupableTime,
} from 'reducks/store/selectors'
const daysOfWeek = new DaysOfWeek()

const useBusinessTimes = () => {
  const openTime: OpenTimeType = useSelector((state: State) => getOpenTime(state))
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const lastPickupTime: BusinessTimeType = useSelector((state: State) => getLastPickupTime(state))
  const relativePickupableTime: BusinessTimeType = useSelector((state: State) => getRelativePickupableTime(state))

  const [businessTimes, setBusinessTimes] = useState<HandleBusinessTimeType[]>([])

  const handleBusinessTimes = useCallback(
    (index: number, key: BusinessTimesKey, value: string) => {
      setBusinessTimes((prevState) => {
        const nextState = prevState
        switch (key) {
          case 'open_time':
            nextState[index].open_time = value
            break
          case 'order_start_time':
            nextState[index].order_start_time = value
            break
          case 'last_order_time':
            nextState[index].last_order_time = value
            break
          case 'last_pickup_time':
            nextState[index].last_pickup_time = value
            break
          case 'relative_pickupable_time':
            nextState[index].relative_pickupable_time = value
            break
          default:
            break
        }
        return [...nextState]
      })
    },
    [setBusinessTimes]
  )

  const addBusinessTime = useCallback(() => {
    setBusinessTimes((prevState) => {
      return [
        ...prevState,
        {
          days_of_week: [],
          open_time: '',
          order_start_time: '',
          last_order_time: '',
          last_pickup_time: '',
          relative_pickupable_time: '0',
        },
      ]
    })
  }, [setBusinessTimes])

  const selectDaysOfWeek = useCallback(
    (dayOfWeek: DayOfWeekType, index: number) => {
      setBusinessTimes((prevState) => {
        const nextState = prevState
        const prevDaysOfWeek = prevState[index].days_of_week
        if (prevState[index].days_of_week.includes(dayOfWeek)) {
          nextState[index].days_of_week = prevDaysOfWeek.filter((_dayOfWeek) => _dayOfWeek !== dayOfWeek)
        } else {
          nextState[index].days_of_week = [...prevDaysOfWeek, dayOfWeek]
        }
        return [...nextState]
      })
    },
    [setBusinessTimes]
  )

  const selectAllDaysOfWeek = useCallback(
    (daysOfWeek: DayOfWeekType[], index: number) => {
      setBusinessTimes((prevState) => {
        prevState[index].days_of_week = [...daysOfWeek]
        return [...prevState]
      })
    },
    [setBusinessTimes]
  )

  const unselectAllDaysOfWeek = useCallback(
    (index: number) => {
      setBusinessTimes((prevState) => {
        if (prevState.length === 1) {
          prevState[index].days_of_week = []
          return [...prevState]
        } else {
          const nextState = prevState.filter((businessTime, i) => i !== index)
          return [...nextState]
        }
      })
    },
    [setBusinessTimes]
  )

  // 時刻データを編集用のデータに置き換える
  useFormatBusinessTimes(
    daysOfWeek.getDaysOfWeek(),
    openTime,
    orderStartTime,
    lastOrderTime,
    lastPickupTime,
    relativePickupableTime,
    setBusinessTimes
  )

  return {
    addBusinessTime,
    businessTimes,
    handleBusinessTimes,
    selectAllDaysOfWeek,
    selectDaysOfWeek,
    setBusinessTimes,
    unselectAllDaysOfWeek,
  }
}

export default useBusinessTimes
